<template>

  <div class="section">
    <div class="ai_chat">
      <div class="ai_head">
        <div class="title">AI Assistant</div>
        <div class="head_controls">
          <select class="model_select" v-model="ai_model" :disabled="ai_loading" title="Model">
            <option value="gpt-4o-mini">gpt-4o-mini — fastest &amp; cheapest</option>
            <option value="gpt-4o">gpt-4o — balanced</option>
            <option value="gpt-5">gpt-5 — most capable (default)</option>
          </select>
          <button class="reset" type="button" @click.stop="reset()" :disabled="ai_loading">New conversation</button>
        </div>
      </div>

      <div class="messages" ref="messagesEl">
        <div v-if="messages.length===0" class="empty">
          Ask Monica (the InterAct admin assistant) anything — students, groups, schedules…
        </div>
        <div v-for="(m,i) in messages" :key="i" :class="['msg', m.role]">
          <div class="who">{{ m.role==='user' ? 'You' : 'Monica' }}</div>
          <div class="bubble">{{ m.text }}</div>
        </div>
        <div v-if="ai_loading" class="msg assistant">
          <div class="who">Monica</div>
          <div class="bubble typing">…thinking</div>
        </div>
      </div>

      <form class="composer" @submit.prevent="ai()">
        <input v-model="ai_query" placeholder="Type a message…" :disabled="ai_loading" autocomplete="off">
        <button type="submit" :disabled="ai_loading || !ai_query">Send</button>
      </form>
    </div>
  </div>

  <div class="section">
    <TeamsQuickMeet/>
  </div>

  <div class="section">
    <Cards/>
  </div>


  <div class="section">
    <Tickets/>
  </div>

  <div class="section">
    <Collection/>
  </div>

  <div class="section">
    <Instructors/>
  </div>


  <div class="section">
    <Leads/>
  </div>

  <div class="section">
    <AttendanceReport/>
  </div>


<router-link to="/database">Database</router-link>


</template>

<script setup>
  import {ref, nextTick} from 'vue'
  import {useStore} from 'vuex'
  import axios from 'axios'
  import Cards from '@/components/DashboardCard.vue'

  import Instructors from '@/components/Instructors.vue'

  import Collection from '@/components/Collection.vue'
  import Tickets from '@/components/Tickets.vue'
  import AttendanceReport from '@/components/AttendanceReport.vue'
  import Leads from '@/components/Leads.vue'
  import TeamsQuickMeet from '@/components/TeamsQuickMeet.vue'


  const store=useStore()

  let ai_query=ref('')
  let ai_model=ref('gpt-5')
  let messages=ref([])
  let ai_loading=ref(false)
  const messagesEl=ref(null)

  async function scrollDown(){
    await nextTick()
    const el=messagesEl.value
    if(el){ el.scrollTop=el.scrollHeight }
  }

  async function ai(){
    const text=(ai_query.value||'').trim()
    if(!text || ai_loading.value){ return }
    messages.value.push({role:'user', text})
    ai_query.value=''
    ai_loading.value=true
    await scrollDown()
    try{
      const {data}=await axios.post('/ai/chat/',{
        body:text,
        model:ai_model.value,
        user_pk: store.state.user?.pk ?? store.state.user?.id,
      })
      messages.value.push({role:'assistant', text:data.message})
    }catch(e){
      messages.value.push({role:'assistant', text:'⚠️ '+(e.response?.data?.message || e.message)})
    }finally{
      ai_loading.value=false
      await scrollDown()
    }
  }

  async function reset(){
    try{
      await axios.post('/ai/reset_thread/', { user_pk: store.state.user?.pk ?? store.state.user?.id })
    }catch(e){ /* ignore */ }
    messages.value=[]
  }




</script>



<style scoped>







  .section{
    width:100vw;
    background:var(--blue);
    color:var(--white);
    overflow: hidden;

  }

  .section:nth-child(2n){
    background:var(--red);
  }




  .ai_chat{
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 760px;
    margin: 0 auto;
    padding: 16px;
  }

  .ai_chat .title{
    font-weight: bold;
    font-size: 1.2rem;
  }

  .ai_head{
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .ai_head .head_controls{
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ai_head .model_select{
    border: 1px solid rgba(255,255,255,0.6);
    background: transparent;
    color: var(--white);
    border-radius: 8px;
    padding: 6px 10px;
    cursor: pointer;
    font-size: 0.85rem;
  }
  .ai_head .model_select option{ color: #000; }
  .ai_head .model_select:disabled{ opacity: 0.5; cursor: not-allowed; }

  .ai_head .reset{
    border: 1px solid rgba(255,255,255,0.6);
    background: transparent;
    color: var(--white);
    border-radius: 8px;
    padding: 6px 12px;
    cursor: pointer;
    font-size: 0.85rem;
  }
  .ai_head .reset:disabled{ opacity: 0.5; cursor: not-allowed; }

  .messages{
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 340px;
    overflow-y: auto;
    padding: 12px;
    background: rgba(255,255,255,0.12);
    border-radius: 10px;
  }

  .messages .empty{
    margin: auto;
    text-align: center;
    opacity: 0.8;
  }

  .msg{
    display: flex;
    flex-direction: column;
    max-width: 80%;
  }

  .msg.user{
    align-self: flex-end;
    align-items: flex-end;
  }

  .msg.assistant{
    align-self: flex-start;
    align-items: flex-start;
  }

  .msg .who{
    font-size: 0.72rem;
    opacity: 0.75;
    margin: 0 4px 2px;
  }

  .msg .bubble{
    padding: 8px 12px;
    border-radius: 12px;
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.35;
    text-align: left;
  }

  .msg.user .bubble{
    background: var(--white);
    color: var(--blue);
    border-bottom-right-radius: 3px;
  }

  .msg.assistant .bubble{
    background: rgba(0,0,0,0.28);
    color: var(--white);
    border-bottom-left-radius: 3px;
    /* monospace + preserved whitespace so Monica's ASCII tables line up */
    font-family: ui-monospace, "Cascadia Code", "Source Code Pro", Menlo, Consolas, monospace;
    font-size: 0.9em;
    overflow-x: auto;
  }

  .bubble.typing{
    opacity: 0.7;
    font-style: italic;
  }

  .composer{
    display: flex;
    gap: 8px;
  }

  .composer input{
    flex: 1;
    padding: 10px 12px;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    color: #222;
  }

  .composer button{
    padding: 10px 18px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    background: var(--white);
    color: var(--blue);
    font-weight: bold;
  }

  .composer button:disabled{
    opacity: 0.5;
    cursor: not-allowed;
  }


</style>
